import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';

import { FBoxContainer, FHTextarea } from '../../../../../base';
import AssesmentbBarriers from '../AssessmentReview/components/AssesmentBarriers';
import { useTreatmentPlanEditable } from '../../../hooks/useTreatmentPlanEditable';
import { assessmentActions } from '../../../../../../store';
import {
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../../../helpers/validation-helpers';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../../../constants';
import { assessmentSummarySchema } from '../../utils/validationSchema';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from '../../constants/states';
import { debounce } from '../../../../../../helpers';
import { FORTA_ERROR, FORTA_BLUE } from '../../../../../../utils/colors';
import ErrorIcon from '../../../../../../assets/images/error-alert.svg';

import type { ChangeEvent } from 'react';
import type { IAssessmentSummary, IMaladaptiveBehavior } from '../../../../../../utils/types';

function AssessmentSummary() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { summary, assessment, persistDataState, triggerValidationsOnPageLoad, errorState } =
    useSelector((state: any) => state.assessments);
  const { list: maladaptiveBehaviors } = useSelector((state: any) => state.maladaptiveBehavior);
  const [symptoms, setSymptoms] = useState<Array<string>>([]);

  useEffect(() => {
    handleValidate(summary);
  }, [summary]);

  const [summaryInfo, setSummaryInfo] = useState<IAssessmentSummary>({
    summary_observations_during_assessment: '',
    summary_assessment_result: '',
    summary_priorities_based_on_assessment: '',
  });
  const [errors, setErrors] = useState<any>({});

  const { id: planId } = treatmentPlan;
  const editable = useTreatmentPlanEditable();

  const {
    summary_assessment_result,
    summary_priorities_based_on_assessment,
    summary_observations_during_assessment,
  } = summary;

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [ERROR_STATES[STATE_IDS.ASSESMENT_SUMMARY]]: errorCount,
        },
      }),
    );
  };

  const updatePersistedData = (summaryData: IAssessmentSummary) => {
    const isValid = validateSync(assessmentSummarySchema, summaryData);
    const data = isValid ? null : summaryData;
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [PERSIST_DATA_STATES[STATE_IDS.ASSESMENT_SUMMARY]]: data,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);
  const debouncedDataPersister = useCallback(debounce(updatePersistedData, 500), []);

  useEffect(() => {
    const persistedData = persistDataState[PERSIST_DATA_STATES[STATE_IDS.ASSESMENT_SUMMARY]];
    if (!persistedData) {
      setErrors({});
      return;
    }

    setSummaryInfo(persistedData);
    const errorsData: { [key: string]: boolean } = getAllInvalidFieldNames(
      assessmentSummarySchema,
      persistedData,
    );
    setErrors(errorsData);
    // eslint-disable
  }, [planId, persistDataState]);

  useEffect(() => {
    const persistedData = persistDataState[PERSIST_DATA_STATES[STATE_IDS.ASSESMENT_SUMMARY]];
    if (persistedData) return;
    const data = {
      summary_assessment_result,
      summary_priorities_based_on_assessment,
      summary_observations_during_assessment,
    };
    setSummaryInfo(data);
    if (triggerValidationsOnPageLoad) {
      const errorObject = getAllInvalidFieldNames(assessmentSummarySchema, data);
      setErrors(errorObject);
    }
    // eslint-disable
  }, [
    summary_assessment_result,
    summary_priorities_based_on_assessment,
    summary_observations_during_assessment,
  ]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  useEffect(() => {
    const symptomsVal = Array.from(
      new Set<string>(
        maladaptiveBehaviors.map((ml: IMaladaptiveBehavior) => ml.associated_dsm5_symptom),
      ),
    );
    setSymptoms(symptomsVal);
  }, [maladaptiveBehaviors]);

  const handleFieldUpdate = (field: string, value: string) => {
    const updatedData = { ...summaryInfo, [field]: value };
    setSummaryInfo(updatedData);
    debouncedDataPersister(updatedData);
    handleValidate(updatedData);
  };

  const handleValidate = (data: IAssessmentSummary = summaryInfo) => {
    if (!data) return;

    const invalidFields: { [key: string]: boolean } = getAllInvalidFieldNames(
      assessmentSummarySchema,
      data,
    );

    setErrors(invalidFields);
  };

  const handleUpdateSummary = () => {
    const isValid = validateSync(assessmentSummarySchema, summaryInfo);
    if (!isValid || !assessment.id || !planId) return;

    dispatch(
      assessmentActions.updateSummary({
        data: summaryInfo,
        getAccessTokenSilently,
        planId,
        assessmentId: assessment.id,
      }),
    );
  };

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Summary of assessment" sx={{ mt: 3 }}>
      {editable && (
        <Typography
          textAlign={'left'}
          alignSelf="start"
          fontWeight={'700'}
          lineHeight={'21px'}
          marginLeft={'0px'}
          mb={3}
          sx={{
            color:
              errorState['assessmentSummary'] > 0 || errorState['assessmentBarrier-new'] > 0
                ? FORTA_ERROR
                : FORTA_BLUE.light1,
          }}
        >
          {(errorState['assessmentSummary'] > 0 || errorState['assessmentBarrier-new'] > 0) && (
            <img
              style={{ width: '20px', height: '20px', marginRight: '8px', marginTop: '-4px' }}
              src={ErrorIcon}
              alt="attention-icon"
            />
          )}
          Attention: All fields with * are required to be filled
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Assessment results in terms of domains of deficit vs strengths:*
          </Typography>
          <FHTextarea
            data-testid="deficitVsStrengthTextfield"
            fullWidth
            minRows={3}
            maxRows={6}
            colorTheme={Themes.Primary}
            disabled={!editable}
            expandable={editable}
            minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
            value={summaryInfo.summary_assessment_result || ''}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldUpdate('summary_assessment_result', e.target.value)
            }
            onBlur={handleUpdateSummary}
            onCloseExpandDialog={handleUpdateSummary}
            error={errors['summary_assessment_result']}
            helperText={
              errors['summary_assessment_result']
                ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                : ''
            }
          />
        </Box>
        {symptoms.length > 0 && (
          <Box mt={2}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Patient symptoms noted from DSM5 in assessment:
            </Typography>
            <Box>
              {symptoms.map((symptom: string) => (
                <Typography key={symptom} textAlign="left" mb={1}>
                  {symptom}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        <Box mt={2}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Priorities/ goals based on assessment results:*
          </Typography>
          <FHTextarea
            data-testid="prioritiesVsGoalsTextField"
            fullWidth
            minRows={3}
            maxRows={6}
            colorTheme={Themes.Primary}
            disabled={!editable}
            expandable={editable}
            minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
            value={summaryInfo.summary_priorities_based_on_assessment || ''}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldUpdate('summary_priorities_based_on_assessment', e.target.value)
            }
            onBlur={handleUpdateSummary}
            onCloseExpandDialog={handleUpdateSummary}
            error={errors['summary_priorities_based_on_assessment']}
            helperText={
              errors['summary_priorities_based_on_assessment']
                ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                : ''
            }
          />
        </Box>
        <Box mt={2}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Barriers noted during assessment:
          </Typography>
          <AssesmentbBarriers />
        </Box>
        <Box mt={2}>
          <Typography fontWeight="bold" mb={1} textAlign="left">
            Observations noted during assessment:*
          </Typography>
          <FHTextarea
            data-testid="observationNotedTextField"
            fullWidth
            minRows={3}
            maxRows={6}
            colorTheme={Themes.Primary}
            disabled={!editable}
            expandable={editable}
            minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
            value={summaryInfo.summary_observations_during_assessment || ''}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldUpdate('summary_observations_during_assessment', e.target.value)
            }
            onBlur={handleUpdateSummary}
            onCloseExpandDialog={handleUpdateSummary}
            error={errors['summary_observations_during_assessment']}
            helperText={
              errors['summary_observations_during_assessment']
                ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                : ''
            }
          />
        </Box>
      </Box>
    </FBoxContainer>
  );
}

export default AssessmentSummary;
